import type { RouteObject } from 'react-router-dom';

import { MotionLazy } from '@mfw/components';
import { Outlet } from 'react-router-dom';

import AuthenticatedGuard from '../pages/Guards/AuthenticatedGuard';
import BreadcrumbsProvider from '../context/BreadcrumbsProvider';
import { crudChildren, crudChildrenWithPreview } from './routes/helpers';
import {
  bookingVipRoute,
  marketplaceRoute,
  premiumRoute,
} from './routes/featureRoutes';
import { organizationRoutes } from './routes/organizationRoutes';
import AppLayout from '../pages/Layouts/AppLayout/AppLayout';
import { publicSiteRoutes } from './routes/publicSiteRoutes';
import { publicRoutes } from './routes/publicRoutes';
import {
  PendingScheduleQuestionnaireModal,
  ForcedSwitchProfileModal,
  PrivacyPolicyModal,
} from '../allFeatures';
import { adminRoutes } from './routes/adminRoutes';
import RouteErrorPage from '../pages/RouteErrorPage';
import NotFoundPage from '../pages/NotFoundPage';
import { authRoutes } from './routes/authRoutes';
import { lazyRoute } from './routes/lazyRoute';
import { ProgressBar } from '../components';
import {
  AppIdentityProviders,
  AppProviders,
  AppSessionProviders,
} from '../context';

export const routes = [
  {
    children: [{ index: true, lazy: () => lazyRoute('/PrivacyPolicyPage') }],
    element: (
      <BreadcrumbsProvider>
        <MotionLazy>
          <ProgressBar />
          <AppProviders>
            <Outlet />
          </AppProviders>
        </MotionLazy>
      </BreadcrumbsProvider>
    ),
    errorElement: <RouteErrorPage />,
    path: '/privacyPolicy',
  },
  {
    children: [
      {
        index: true,
        lazy: () => lazyRoute('/TermsOfUseConfirmContactEmailPage'),
      },
    ],
    element: (
      <BreadcrumbsProvider>
        <MotionLazy>
          <ProgressBar />
          <AppProviders>
            <Outlet />
          </AppProviders>
        </MotionLazy>
      </BreadcrumbsProvider>
    ),
    errorElement: <RouteErrorPage />,
    // Must match the backend's `appConfig.touContactEmailConfirmLink` default.
    path: '/terms-of-use/confirm-contact-email',
  },
  publicSiteRoutes,
  {
    // PR-2.1: ONE identity owner for the whole app, `/auth/*` included. These used to be
    // two top-level routes with a provider stack each, so walking out of the gate mounted
    // a second `AuthProvider` and paid a second Bearer `/auth/me` for a login the guard
    // had already confirmed (docs/review/auth-1.md I0). Now the shell is rendered once,
    // here, and each branch keeps its own keyed `AppSessionProviders` — the account-scoped
    // reset on `/auth ↔ /` that the split used to give for free.
    children: [
      {
        children: [
          { index: true, lazy: () => lazyRoute('/DashboardPage') },
          {
            lazy: () => lazyRoute('/Auth/VerificationPage'),
            path: '/verification',
          },
          marketplaceRoute,
          { lazy: () => lazyRoute('/SavedPage/SavedPage'), path: '/saved' },
          // R-AUD-84: org-less education viewer — a user without an organization
          // views purchased education outside of OrganizationGuard (the BE gate is
          // ownership-based: author or ContentLibrary row; premium-content
          // workstream also unlocks premium items for active subscribers).
          {
            handle: { title: 'view', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/EpisodesPages/EpisodeViewerPage'),
            path: '/education/episodes/:id/view',
          },
          {
            handle: { title: 'view', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/ModulesPages/ModuleViewerPage'),
            path: '/education/modules/:id/view',
          },
          {
            handle: { title: 'view', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/CoursesPages/CourseViewerPage'),
            path: '/education/courses/:id/view',
          },
          // premium-content workstream: org-less education BUILDER routes so
          // platform admins (who have no organization) can author content
          {
            handle: { title: 'add', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/EpisodesPages/EpisodePage'),
            path: '/education/episodes/new',
          },
          {
            handle: { title: 'edit', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/EpisodesPages/EpisodePage'),
            path: '/education/episodes/:id',
          },
          {
            handle: { title: 'add', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/ModulesPages/ModulePage'),
            path: '/education/modules/new',
          },
          {
            handle: { title: 'edit', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/ModulesPages/ModulePage'),
            path: '/education/modules/:id',
          },
          {
            handle: { title: 'add', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/CoursesPages/CoursePage'),
            path: '/education/courses/new',
          },
          {
            handle: { title: 'edit', hideBreadcrumbs: true },
            lazy: () => lazyRoute('/CoursesPages/CoursePage'),
            path: '/education/courses/:id',
          },
          {
            handle: { title: 'wallet' },
            lazy: () => lazyRoute('/WalletPage/WalletPage'),
            path: '/wallet',
          },
          premiumRoute,
          {
            // Direct sidebar destination ("Statistics") with no
            // drill-down of its own, so no `handle.title` — a bare
            // "Home / Statistics" would only pretend it's nested under Home,
            // which it isn't (sibling sidebar items). Same reasoning as the
            // org-scoped `home`/`schedule`/etc. in `organizationRoutes.tsx`.
            lazy: () => lazyRoute('/StatsPages/StatsPage'),
            path: '/stats',
          },
          {
            // F5: personal cross-org schedule (aggregated calendar + self-assign,
            // no organization required) — sits outside OrganizationGuard just
            // like `/stats`, since it aggregates events across all of the user's
            // organizations (and org-less self-assigned events) via
            // `/v2/auth/me/schedules`. Direct sidebar destination ("My
            // calendar") with no drill-down, so no `handle.title` either.
            lazy: () => lazyRoute('/SchedulePages/PersonalSchedulePage'),
            path: '/schedule',
          },
          {
            // Dedicated page behind Home's "Book a training" carousel ("View
            // all") — same tier as `/schedule`, since it aggregates bookable
            // sessions across every organization rather than belonging to one.
            handle: { title: 'bookingEvents' },
            lazy: () => lazyRoute('/BookingEventsPages/BookingEventsPage'),
            path: '/booking-events',
          },
          // The shareable privileged-seat link (`sessions` switch).
          bookingVipRoute,
          // premium-content workstream: the personal mounts get full CRUD children
          // (same pages as the org mounts) so platform admins without an
          // organization can author org-less content
          {
            // org-less Library (tabs: exercises/workouts/programs) — the
            // sidebar's single "Workout Content" row for platform users
            children: [
              {
                index: true,
                lazy: () => lazyRoute('/LibraryPage/LibraryPage'),
              },
            ],
            path: '/library',
          },
          {
            children: crudChildren(
              '/ExercisesPages/ExercisesPages',
              '/ExercisesPages/ExercisePage',
              { hideBreadcrumbs: true },
            ),
            handle: { title: 'exercises' },
            path: '/exercises',
          },
          {
            children: crudChildrenWithPreview(
              '/WorkoutPages/WorkoutsPage',
              '/WorkoutPages/WorkoutPage',
              ':workoutId',
              { hideBreadcrumbs: true },
            ),
            handle: { title: 'workouts' },
            path: '/workouts',
          },
          {
            children: crudChildrenWithPreview(
              '/ProgramsPages/ProgramsPage',
              '/ProgramsPages/ProgramPage',
              ':programId',
              { hideBreadcrumbs: true },
            ),
            handle: { title: 'programs' },
            path: '/programs',
          },
          {
            children: [
              {
                handle: { title: 'add', hideBreadcrumbs: true },
                lazy: () => lazyRoute('/GroceriesPages/GroceryPage'),
                path: 'new',
              },
              {
                handle: { title: 'edit', hideBreadcrumbs: true },
                lazy: () => lazyRoute('/GroceriesPages/GroceryPage'),
                path: ':id',
              },
            ],
            handle: { title: 'groceries' },
            path: '/groceries',
          },
          {
            children: crudChildren(
              '/MealsPages/MealsPage',
              '/MealsPages/MealPage',
              { hideBreadcrumbs: true },
            ),
            handle: { title: 'meals' },
            path: '/meals',
          },
          {
            children: crudChildren(
              '/NutritionPlansPages/NutritionPlansPage',
              '/NutritionPlansPages/NutritionPlanPage',
              { hideBreadcrumbs: true },
            ),
            handle: { title: 'nutrition-plans' },
            path: '/nutrition-plans',
          },
          {
            handle: { title: 'nutrition' },
            lazy: () => lazyRoute('/NutritionPage/NutritionRoutePage'),
            path: '/nutrition',
          },
          {
            handle: { title: 'education' },
            lazy: () => lazyRoute('/EducationPage/EducationRoutePage'),
            path: '/education',
          },
          {
            children: adminRoutes,
            handle: { title: 'admin', breadcrumbTo: false },
            path: 'admin',
          },
          organizationRoutes,
          {
            children: [
              {
                children: [
                  {
                    index: true,
                    lazy: () => lazyRoute('/Profiles/AccountPage/AccountPage'),
                  },
                ],
                path: ':userId',
              },
            ],
            handle: { title: 'me' },
            path: 'me',
          },
          {
            // F4: `member` accounts (trainer/admin waiting to be invited into an
            // org) land here after onboarding. Outside OrganizationGuard — no org
            // context required; sits in the AppLayout tree so the already-global
            // InvitationModal surfaces the incoming invite here too.
            lazy: () =>
              lazyRoute('/PendingInvitationPage/PendingInvitationPage'),
            path: 'pending-invitation',
          },
          {
            // F7: "Add profile" — create an ADDITIONAL org/PT profile from
            // inside the app, reached from the switcher. Same pattern as
            // /pending-invitation above: outside OrganizationGuard, no org
            // context required to create a new one.
            lazy: () => lazyRoute('/AddProfilePage/AddProfilePage'),
            path: 'profiles/new',
          },
          publicRoutes,
        ],
        element: (
          <AppSessionProviders key="app">
            <AuthenticatedGuard>
              <PrivacyPolicyModal />
              <PendingScheduleQuestionnaireModal />
              <ForcedSwitchProfileModal />
              <AppLayout />
            </AuthenticatedGuard>
          </AppSessionProviders>
        ),
        path: '/',
      },
      {
        children: authRoutes,
        element: (
          // Listens for the install offer but never shows it: this tree is
          // replaced right after sign-in or onboarding (see ManifestProvider).
          <AppSessionProviders key="auth" canShowAppInstallPrompt={false}>
            <AuthenticatedGuard>
              <Outlet />
            </AuthenticatedGuard>
          </AppSessionProviders>
        ),
        path: 'auth',
      },
    ],
    element: (
      <BreadcrumbsProvider>
        <MotionLazy>
          {/* One position for the bar, which used to sit before the providers on `/`
              and after them on `/auth/*`. */}
          <ProgressBar />
          <AppIdentityProviders>
            <Outlet />
          </AppIdentityProviders>
        </MotionLazy>
      </BreadcrumbsProvider>
    ),
    errorElement: <RouteErrorPage />,
  },
  {
    element: (
      <BreadcrumbsProvider>
        <MotionLazy>
          <AppProviders>
            <AuthenticatedGuard>
              <NotFoundPage />
            </AuthenticatedGuard>
            <ProgressBar />
          </AppProviders>
        </MotionLazy>
      </BreadcrumbsProvider>
    ),
    errorElement: <RouteErrorPage />,
    path: '*',
  },
] satisfies RouteObject[];
